import { useEffect, useState } from "react";
import { MapPin, Zap, TrendingUp } from "lucide-react";
import { getWhySolarNow } from "@/lib/api";

const CORRIDORS = [
  // Approximate stylized positions on the 100x100 VA silhouette (not geographically exact).
  { name: "Ashburn", count: 140, cx: 71, cy: 30 },
  { name: "Richmond", count: 134, cx: 58, cy: 62 },
  { name: "Sterling", count: 88, cx: 69, cy: 31 },
  { name: "Manassas", count: 63, cx: 64, cy: 35 },
  { name: "Fredericksburg", count: 59, cx: 62, cy: 44 },
  { name: "Dulles", count: 30, cx: 70, cy: 32 },
  { name: "Culpeper", count: 30, cx: 52, cy: 42 },
  { name: "Gainesville", count: 24, cx: 63, cy: 37 },
  { name: "Chantilly", count: 21, cx: 68, cy: 33 },
  { name: "Bristow", count: 18, cx: 65, cy: 37 },
  { name: "Herndon", count: 12, cx: 70, cy: 34 },
  { name: "Vienna", count: 9, cx: 72, cy: 34 },
  { name: "Norfolk", count: 7, cx: 78, cy: 74 },
  { name: "Reston", count: 6, cx: 70, cy: 33 },
  { name: "Roanoke", count: 5, cx: 32, cy: 60 },
  { name: "Charlottesville", count: 4, cx: 50, cy: 54 },
  { name: "Harrisonburg", count: 3, cx: 40, cy: 44 },
];

const TOTAL_DCS = 674;
const TOTAL_MARKETS = 25;

const DRIVERS = [
  "New power plants and generation capacity",
  "Transmission-line construction",
  "Grid reliability upgrades",
  "Fuel and purchased-power expenses",
  "Long-term infrastructure cost recovery",
  "Greater regional electricity demand",
];

export default function VirginiaDataCenterMap() {
  const [data, setData] = useState(null);
  useEffect(() => { getWhySolarNow().then(setData).catch(() => {}); }, []);

  return (
    <section className="py-20 md:py-28 bg-[#0B261E] text-[#F8FAFC]" data-testid="why-solar-now-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <div className="eyebrow" style={{ color: "#FEF3C7" }}>Why Solar Now</div>
          <h2 className="mt-3 font-serif-display text-3xl md:text-5xl font-semibold">
            Virginia's Energy Demand Is Growing
          </h2>
          <p className="mt-5 text-lg text-[#F8FAFC]/80 leading-relaxed">
            Virginia is the world's largest data-center market — <span className="font-mono-metric text-[#FEF3C7]">674 facilities across 25 markets</span>, most concentrated in Ashburn's "Data Center Alley." Growing demand may require new generation, transmission infrastructure, and long-term grid investments that can flow through to residential rates.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="why-solar-now-stat-1">
              <div className="flex items-center gap-2 text-[#FEF3C7]">
                <Zap className="w-4 h-4" />
                <div className="text-xs uppercase tracking-widest font-mono-metric">Forecast · 2025</div>
              </div>
              <div className="font-serif-display text-3xl mt-3">
                {data?.dominion_forecast_gw_2025 ?? "4.2"} GW
              </div>
              <div className="text-sm text-[#F8FAFC]/70 mt-1">Dominion data-center billing demand</div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="why-solar-now-stat-2">
              <div className="flex items-center gap-2 text-[#FEF3C7]">
                <TrendingUp className="w-4 h-4" />
                <div className="text-xs uppercase tracking-widest font-mono-metric">By 2040 (est.)</div>
              </div>
              <div className="font-serif-display text-3xl mt-3 font-mono-metric">
                ${data?.estimated_impact_by_2040?.low ?? 14}–${data?.estimated_impact_by_2040?.high ?? 37}/mo
              </div>
              <div className="text-sm text-[#F8FAFC]/70 mt-1">Est. generation & transmission impact per Dominion residential customer</div>
            </div>
          </div>

          <div className="mt-8">
            <div className="eyebrow" style={{ color: "#FEF3C7" }}>Potential Cost Drivers</div>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2">
              {DRIVERS.map((d) => (
                <li key={d} className="flex items-start gap-2 text-sm text-[#F8FAFC]/85">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D97706] mt-2 shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="rounded-3xl bg-white/5 border border-white/10 p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <div className="eyebrow" style={{ color: "#FEF3C7" }}>Data Center Alley</div>
                <div className="font-serif-display text-2xl mt-1">The world's largest DC market</div>
              </div>
              <div className="flex gap-2">
                <div className="rounded-xl bg-[#D97706]/15 border border-[#D97706]/30 px-3 py-2 text-center min-w-[70px]">
                  <div className="font-serif-display text-xl text-[#FEF3C7] leading-none">{TOTAL_DCS}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono-metric text-[#FEF3C7]/80 mt-1">Data Centers</div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-center min-w-[70px]">
                  <div className="font-serif-display text-xl leading-none">{TOTAL_MARKETS}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono-metric text-[#F8FAFC]/70 mt-1">VA Markets</div>
                </div>
              </div>
            </div>

            <svg viewBox="0 0 100 100" className="w-full h-auto" role="img" aria-label="Illustrative map of Virginia data-center markets. Total 674 facilities across 25 markets, concentrated in Northern Virginia." style={{ overflow: "visible" }}>
              {/* Rough VA silhouette (stylized, not to scale) */}
              <path
                d="M 8 30 L 30 22 L 50 20 L 72 24 L 88 32 L 92 42 L 88 55 L 78 68 L 66 78 L 52 82 L 40 78 L 28 70 L 18 58 L 10 46 Z"
                fill="rgba(15,56,44,0.6)"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth="0.4"
              />
              {/* Northern VA "Data Center Alley" halo */}
              <circle cx="69" cy="32" r="13" fill="#D97706" opacity="0.08" />
              <circle cx="69" cy="32" r="9" fill="#D97706" opacity="0.10" />
              {CORRIDORS.map((c) => {
                const r = Math.max(1.3, Math.sqrt(c.count) * 0.5);
                return (
                  <g key={c.name}>
                    <circle cx={c.cx} cy={c.cy} r={r + 1.5} fill="#D97706" opacity="0.22">
                      <animate attributeName="opacity" values="0.35;0.08;0.35" dur="3s" repeatCount="indefinite" />
                    </circle>
                    <circle cx={c.cx} cy={c.cy} r={r} fill="#FEF3C7" stroke="#D97706" strokeWidth="0.3" />
                  </g>
                );
              })}
              {/* Ashburn callout */}
              <line x1="71" y1="30" x2="78" y2="14" stroke="rgba(254,243,199,0.4)" strokeWidth="0.3" strokeDasharray="1 1" />
              <text x="78" y="12" fill="#FEF3C7" fontSize="3" fontFamily="JetBrains Mono" textAnchor="start">Ashburn · 140</text>
              <text x="78" y="16" fill="#FEF3C7" fontSize="2.2" opacity="0.7" fontFamily="JetBrains Mono" textAnchor="start">Data Center Alley</text>
              {/* Richmond callout */}
              <line x1="58" y1="62" x2="42" y2="72" stroke="rgba(254,243,199,0.4)" strokeWidth="0.3" strokeDasharray="1 1" />
              <text x="18" y="73" fill="#FEF3C7" fontSize="3" fontFamily="JetBrains Mono" textAnchor="start">Richmond · 134</text>
            </svg>

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-widest font-mono-metric text-[#FEF3C7]/70 mb-3">Top Markets by Data-Center Count</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {CORRIDORS.slice(0, 10).map((c) => (
                  <div key={c.name} className="flex items-center justify-between text-xs" data-testid={`dc-market-${c.name.toLowerCase()}`}>
                    <div className="flex items-center gap-2 text-[#F8FAFC]/85">
                      <div className="w-2 h-2 rounded-full bg-[#D97706]" />
                      <span>{c.name}</span>
                    </div>
                    <div className="font-mono-metric text-[#FEF3C7]">{c.count}</div>
                  </div>
                ))}
              </div>
              {/* Distribution bar showing NoVA concentration */}
              <div className="mt-5">
                <div className="flex justify-between text-[10px] font-mono-metric uppercase tracking-widest text-[#F8FAFC]/60 mb-1.5">
                  <span>Northern Virginia</span>
                  <span>Rest of VA</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden flex">
                  <div className="h-full bg-[#D97706]" style={{ width: "62%" }} />
                  <div className="h-full bg-[#164E3D]" style={{ width: "38%" }} />
                </div>
                <div className="flex justify-between text-[11px] font-mono-metric text-[#F8FAFC]/80 mt-1">
                  <span>~62% of VA data centers</span>
                  <span>~38%</span>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-white/10 text-xs text-[#F8FAFC]/60">
              Source: <a href="https://www.datacentermap.com/usa/virginia/" target="_blank" rel="noreferrer" className="text-[#FEF3C7] hover:text-[#D97706] underline underline-offset-2">datacentermap.com</a> · 674 facilities across 25 Virginia markets · Illustrative positioning
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
